/**
 * @fullvsl/dashboard/analytics-format — pure display helpers for the dashboard.
 *
 * All functions here are pure (no DOM, no I/O) so they are unit-testable and
 * reusable on both the fixture path and the live-API path. They operate on the
 * contracts types (`AnalyticsOverview`, `AnalyticsBreakdown`, `AnalyticsEvent`)
 * so the dashboard never re-derives semantics that FUL-6 already fixed.
 */
import type {
  AnalyticsEvent,
  AnalyticsOverview,
} from "../../contracts/src/contracts";

/* -----------------------------------------------------------------------------------
 * Number / time formatters
 * ----------------------------------------------------------------------------------- */

const NF = new Intl.NumberFormat("pt-BR");

/** 1234567 → "1,234,567". */
export function fmtInt(n: number): string {
  return NF.format(Math.round(n));
}

/** 0.624 → "62.4%". `digits` controls precision. */
export function fmtPct(ratio: number, digits = 1): string {
  if (!Number.isFinite(ratio)) return "—";
  return new Intl.NumberFormat("pt-BR", {
    style: "percent",
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(ratio);
}

/** Seconds → "MM:SS" (or "H:MM:SS" past an hour). */
export function fmtDuration(totalSec: number): string {
  if (!Number.isFinite(totalSec) || totalSec < 0) return "00:00";
  const s = Math.round(totalSec);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  const mm = String(m).padStart(2, "0");
  const ss = String(sec).padStart(2, "0");
  return h > 0 ? `${h}:${mm}:${ss}` : `${mm}:${ss}`;
}

/** ISO ts → "HH:MM:SS" (24h, UTC) for the event-log timestamp column. */
export function fmtClock(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const p = (n: number) => String(n).padStart(2, "0");
  return `${p(d.getUTCHours())}:${p(d.getUTCMinutes())}:${p(d.getUTCSeconds())}`;
}

/** ISO ts → "Aug 1, 2026" style date. */
export function fmtDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("pt-BR", {
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  });
}

/* -----------------------------------------------------------------------------------
 * CTA funnel — conversion-decision view (PRD §10.1 / §10.4)
 * ----------------------------------------------------------------------------------- */

export interface CtaFunnelStep {
  key: "impressions" | "plays" | "cta_shown" | "cta_clicked";
  label: string;
  count: number;
  /** Share of the previous step (cohort conversion), null for the first step. */
  stepRate: number | null;
  /** Share of impressions (of total funnel). */
  ofImpressions: number;
}

/**
 * Build the CTA funnel from the overview. CTA click rate uses the FUL-6 §D
 * conversion proxy (cta_clicked / cta_shown) — the MVP conversion proxy metric.
 */
export function ctaFunnel(o: AnalyticsOverview): CtaFunnelStep[] {
  const impressions = o.impressions;
  const plays = o.plays;
  const ctaShown = o.ctaShown;
  const ctaClicked = o.ctaClicked;
  const ofTotal = (n: number) => (impressions > 0 ? n / impressions : 0);
  const step = (n: number, prev: number) =>
    prev > 0 ? n / prev : null;
  return [
    {
      key: "impressions",
      label: "Impressões",
      count: impressions,
      stepRate: null,
      ofImpressions: 1,
    },
    {
      key: "plays",
      label: "Reproduções",
      count: plays,
      stepRate: step(plays, impressions),
      ofImpressions: ofTotal(plays),
    },
    {
      key: "cta_shown",
      label: "CTA exibida",
      count: ctaShown,
      stepRate: step(ctaShown, plays),
      ofImpressions: ofTotal(ctaShown),
    },
    {
      key: "cta_clicked",
      label: "CTA clicada",
      count: ctaClicked,
      stepRate: step(ctaClicked, ctaShown),
      ofImpressions: ofTotal(ctaClicked),
    },
  ];
}

/* -----------------------------------------------------------------------------------
 * Comportamento de retomada (PRD §10.1)
 * ----------------------------------------------------------------------------------- */

export interface ResumeSummary {
  offered: number;
  accepted: number;
  /** accepted / offered — the resume acceptance rate. */
  acceptanceRate: number;
}

export function resumeSummary(o: AnalyticsOverview): ResumeSummary {
  const { resumeOffered, resumeAccepted } = o;
  return {
    offered: resumeOffered,
    accepted: resumeAccepted,
    acceptanceRate: resumeOffered > 0 ? resumeAccepted / resumeOffered : 0,
  };
}

/* -----------------------------------------------------------------------------------
 * Curva de retenção — display-ready points + drop-off detection (PRD §10.2)
 * ----------------------------------------------------------------------------------- */

export interface RetentionPoint {
  pct: number;
  /** Absolute viewers reaching this decile. */
  retained: number;
  /** Retention ratio vs the first bucket (0%). */
  retention: number;
}

export interface RetentionDisplay {
  points: RetentionPoint[];
  /** Largest single-step drop-off (pct bucket where the most viewers left). */
  biggestDrop: { fromPct: number; toPct: number; lost: number } | null;
  /** Viewers reaching the configured CTA reveal moment (50% by default). */
  viewersAtCta: number;
  /** Viewers who dropped before reaching the CTA reveal. */
  droppedBeforeCta: number;
}

/**
 * Reshape the overview retention curve (0..100 deciles, `retained` counts) into
 * display points + drop-off insights. `ctaRevealPct` matches the CTA config
 * (default 50% — see the demo player / aggregation `cta_shown` threshold).
 */
export function retentionDisplay(
  o: AnalyticsOverview,
  ctaRevealPct = 50,
): RetentionDisplay {
  const curve = o.retentionCurve;
  if (curve.length === 0) {
    return { points: [], biggestDrop: null, viewersAtCta: 0, droppedBeforeCta: 0 };
  }
  const base = curve[0]?.retained ?? 0;
  const points: RetentionPoint[] = curve.map((p) => ({
    pct: p.pct,
    retained: p.retained,
    retention: base > 0 ? p.retained / base : 0,
  }));

  let biggestDrop: RetentionDisplay["biggestDrop"] = null;
  for (let i = 1; i < points.length; i++) {
    const lost = points[i - 1].retained - points[i].retained;
    if (lost > 0 && (!biggestDrop || lost > biggestDrop.lost)) {
      biggestDrop = { fromPct: points[i - 1].pct, toPct: points[i].pct, lost };
    }
  }

  // Interpolate the viewer count at the CTA reveal pct from the decile grid.
  const viewersAtCta = interpolateRetained(curve, ctaRevealPct);
  const started = base;
  const droppedBeforeCta = Math.max(0, started - viewersAtCta);

  return { points, biggestDrop, viewersAtCta, droppedBeforeCta };
}

/** Linear interpolation of `retained` at an arbitrary pct on the decile grid. */
export function interpolateRetained(
  curve: { pct: number; retained: number }[],
  atPct: number,
): number {
  if (curve.length === 0) return 0;
  if (atPct <= curve[0].pct) return curve[0].retained;
  const last = curve[curve.length - 1];
  if (atPct >= last.pct) return last.retained;
  for (let i = 1; i < curve.length; i++) {
    if (atPct <= curve[i].pct) {
      const a = curve[i - 1];
      const b = curve[i];
      const span = b.pct - a.pct || 1;
      const t = (atPct - a.pct) / span;
      return Math.round(a.retained + (b.retained - a.retained) * t);
    }
  }
  return last.retained;
}

/* -----------------------------------------------------------------------------------
 * Breakdown — rank rows by viewer share (PRD §10.3)
 * ----------------------------------------------------------------------------------- */

export interface BreakdownRow {
  key: string;
  count: number;
  share: number;
}

/**
 * Rank a breakdown tally (`bySource` / `byDevice` / `byAspectRatio`) into
 * share-sorted rows. Each visitor is attributed to exactly one bucket by the
 * backend (`computeBreakdown`), so rows sum to the unique-viewer total.
 */
export function rankBreakdown(tally: Record<string, number>): BreakdownRow[] {
  const total = Object.values(tally).reduce((s, n) => s + n, 0);
  return Object.entries(tally)
    .map(([key, count]) => ({
      key,
      count,
      share: total > 0 ? count / total : 0,
    }))
    .sort((a, b) => b.count - a.count);
}

/** Pretty label for raw breakdown keys (device/source/aspect normalization). */
export function breakdownLabel(dim: "device" | "source" | "aspect", key: string): string {
  if (dim === "device") {
    const devices: Record<string, string> = {
      mobile: "Celular",
      desktop: "Computador",
      tablet: "Tablet",
    };
    return devices[key] ?? key.charAt(0).toUpperCase() + key.slice(1);
  }
  if (dim === "aspect") return key;
  // source: turn `facebook_ads` → `Anúncios no Facebook`.
  const sources: Record<string, string> = {
    facebook_ads: "Anúncios no Facebook",
    google_ads: "Anúncios no Google",
    email_broadcast: "Disparo de e-mail",
    organic_search: "Busca orgânica",
    youtube_referral: "Indicação do YouTube",
  };
  if (sources[key]) return sources[key];
  return key
    .split("_")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

/** Two-letter badge for a breakdown key (device/source). */
export function breakdownBadge(dim: "device" | "source", key: string): string {
  if (dim === "device") {
    return key === "mobile" ? "MO" : key === "tablet" ? "TB" : "DT";
  }
  return key
    .split("_")
    .map((w) => w[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
}

/* -----------------------------------------------------------------------------------
 * Registro de eventos — recent conversion signals (PRD §13 "Events" view)
 * ----------------------------------------------------------------------------------- */

const CONVERSION_EVENTS = new Set<AnalyticsEvent["type"]>([
  "cta_clicked",
  "cta_shown",
  "completed",
  "resume_accepted",
]);

/** Slice the most recent N events for the raw event-log table. */
export function recentEvents(
  events: AnalyticsEvent[],
  limit = 12,
): AnalyticsEvent[] {
  return events.slice(0, limit);
}

/** Count conversion-relevant events (CTA + completion + resume). */
export function conversionSignalCount(events: AnalyticsEvent[]): number {
  let n = 0;
  for (const e of events) if (CONVERSION_EVENTS.has(e.type)) n += 1;
  return n;
}

/** Badge color tone for an event type in the log. */
export function eventTone(type: AnalyticsEvent["type"]): "conv" | "neutral" | "warn" {
  if (type === "cta_clicked" || type === "completed" || type === "resume_accepted") {
    return "conv";
  }
  if (type === "error" || type === "pause") return "warn";
  return "neutral";
}
